<template>
  <div class="component" :class="{ 'telehealth-call': telehealthCallStatus }">
    <div class="nav-breadcrumb">
      <NavigationBreadcrumb :navigationWidth="navigationWidth" @toggleParticipant="toggleParticipant" :navigationOptions="navigationOptions"></NavigationBreadcrumb>
    </div>
    <div class="clear-float"></div>
    <PiiAndNonPiiInfo class="clear-both" v-if="participantInfo" :participantDetails="participantInfo" @expanded="piInfoExpanded"></PiiAndNonPiiInfo>
    <b-row class="mt-3" id="edcFormDetail">
      <b-col cols="12" class="p-0">
        <div v-if="generalInfo" class="task-navigation__current-task text-center ml-45 mr-45" id="task-navigation__current-task">
          <span class="current-task__title fw-500 fs-20 text-dark">{{ generalInfo.formName }}</span>
          <br />
          <div class="selectedResponse" ref="responsesDatesDropDown">
            {{ generalInfo.createdTime | formatUTCDate }}
          </div>
        </div>
        <ParticipantInfo
          class="mt-2"
          @verificationConfirmed="verificationConfirmed"
          @approvalConfirmed="approvalConfirmed"
          @reviewConfirmed="reviewConfirmed"
          @lockConfirmed="lockConfirmed"
          @signoffConfirmed="signoffConfirmed"
          v-if="participantInfo && generalInfo"
          :edcFormInfo="generalInfo"
          :participantDetails="participantInfo"
          :selectedLanguageCulture="selectedLanguageCulture"
          :languages="languages"
          :areActionsAllowed="{
            document: false,
            telehealth: true,
            edcforms: false,
            approval: true,
            verifyBtn: true
          }"
          @onLanguageChange="onLanguageChange"
        />
      </b-col>
    </b-row>
    <div v-if="participantInfo">
      <div class="pb-50">
        <b-card no-body class="shadow study-particip-form-block pb-50">
          <b-row v-if="edcFormHistoryVersion" no-gutters>
            <b-col>
              <EdcFormHistoryVersion :edcFormVersionData="edcFormHistoryVersion" :lang="languageCulture" @back="historyBack" />
            </b-col>
          </b-row>
          <b-row v-else no-gutters>
            <b-col
              :class="{
                'col-12': sourceDocumentsOpen,
                'col-8': !sourceDocumentsOpen
              }"
            >
              <b-tabs v-model="tabIndex">
                <b-tab title="Latest Version" @click="openTab(1)">
                  <b-card-group class="pb-135" v-if="!edit" :class="{ 'border-top': canPiAccess }">
                    <b-card no-body class="border-0" :header="formData && formData.name">
                      <b-row>
                        <b-col>
                          <div v-if="checkIfEditCheckAllowed(formData) && (canPiAccess || canSubPiAccess || canScAccess || canRaterAccess || canInterviewerAccess || canHomeHealthAccess || canCallCenterAccess) && !user.readOnly && generalInfo && generalInfo.lockStatus === 'UNLOCKED'" class="float-right my-1">
                            <button-cmp v-if="!edit" @click="edit = !edit" size="sm" variant="outline-secondary">
                              <span>
                                <i class="fa fa-pencil" aria-hidden="true"></i>
                                <span
                                  class="pl-5"
                                  v-translate="{
                                    key: 'button_edit',
                                    val: languageCulture,
                                    html: 'Edit'
                                  }"
                                  >Edit</span
                                >
                              </span>
                            </button-cmp>
                            <strong v-else class="editing">Currently editing</strong>
                          </div>
                        </b-col>
                      </b-row>
                      <template v-if="formData.type == 'EDC'">
                        <SubmittedFormPreview v-if="answersObj" :formData="answersObj" @openQuery="openQuery" :queryIds="queryIds" @selected="filterByField" :selectedId="newQuery || clickedFieldId" />
                      </template>
                      <template v-else>
                        <ActivityFormPreviewColumns
                          :activityFormData="formData"
                          :formName="formData && formData.name"
                          :mode="'RESPONSE'"
                          ref="activityFormPreview"
                          @openQuery="openQuery"
                          :lockStatus="generalInfo && generalInfo.lockStatus"
                          :queryIds="queryIds"
                          :queries="queries"
                          @disabledFields="disabledFields"
                          @selected="filterByField"
                          :selectedId="newQuery || clickedFieldId"
                          :showBanner="false"
                          :createdById="createdById"
                          :lang="languageCulture"
                          :validationCount="validationCount"
                          :completeEdit="edit"
                        />
                      </template>
                    </b-card>
                  </b-card-group>
                  <div v-if="edit">
                    <template v-if="formData.type == 'EDC'">
                      <FormPreview v-if="formData && formData.fields" :formSubmitted="formSubmitted" :formData="formData.fields" :queryTool="true" />
                    </template>
                    <template v-else>
                      <ActivityFormPreviewColumns
                        :activityFormData="formData"
                        :formName="formData && formData.name"
                        :mode="'EDIT'"
                        :lockStatus="generalInfo && generalInfo.lockStatus"
                        ref="activityFormPreview"
                        :trigger-validation="triggerValidation"
                        @disabledFields="disabledFields"
                        :formSubmitted="formSubmitted"
                        :queryTool="true"
                        @getQuestionScores="validateDataForScoring"
                        :showBanner="true"
                        :createdById="createdById"
                        :lang="languageCulture"
                        :validationCount="validationCount"
                        :completeEdit="edit"
                      />
                    </template>
                    <b-row class="my-2 pl-30 pb-15 pt-15" align-v="center">
                      <b-col class="col-6 col-md-3">
                        <button variant="primary" class="btn btn-block fs-14 btn-primary primaryBgColor pl-0 pr-0" @click="openChangeReasonModal">Save</button>
                      </b-col>
                      <b-col class="col-6 col-md-3">
                        <b-link class="fs-14 primary" @click="cancelEdit">Cancel</b-link>
                      </b-col>
                    </b-row>
                  </div>
                </b-tab>
                <b-tab title="History" v-if="!canSupportAccess && formHistory && formHistory.length && checkIfEditCheckAllowed(formData)" @click="checkEditMode">
                  <EdcFormHistoryList :history="formHistory" @view="viewHistoryForm" />
                </b-tab>
                <b-tab title="Source Documents" v-if="getDocuments && (canPiAccess || canSubPiAccess || canStudyAdminAccess || canScAccess || canHomeHealthAccess || canCraAccess || canSiteDataLockAccess || canCallCenterAccess || user.readOnly || canCraUBAccess)" @click="openTab(3)">
                  <EdcFormSourceDocuments :documents="documentsList" :studyId="studyId" :participantId="participantId" :responseId="formResponses.responseId" type="SCHEDULED" @getDocuments="getSourceDocuments" :participantInfo="participantInfo" />
                </b-tab>
                <b-tab title="Additional Scores" v-if="showAdditionalScores" @click="openTab(4)">
                  <EdcFormAdditionalScores :variablesList="variablesList" :studyId="studyId" :activityId="activityId" :participantId="participantId" :activityRevisionId="activityRevisionId" :responseId="responseId" :responseVersionId="responseVersionId" />
                </b-tab>
              </b-tabs>
            </b-col>
            <b-col cols="4" xl="4" class="tabs-queries" v-if="sourceDocumentsOpen === false">
              <EdcFormQueries
                :getOffsetValue="getOffsetValue"
                v-model="newQuery"
                :filter="clickedFieldId"
                @updateQueries="getListOfQueries"
                :studyId="formStudyId"
                @updateComment="updateComment"
                :comments="comments"
                :queries="queries"
                :formId="fId"
                :lockStatus="generalInfo && generalInfo.lockStatus"
                @openQuery="openQuery"
                @createQuery="createQuery"
                @createComment="createComment"
                @resetFilter="resetFilter"
                @onQueryClosed="onQueryClosed"
                @onQueryReopened="onQueryReopened"
                :siteId="participantInfo.siteId"
              />
            </b-col>
          </b-row>
        </b-card>
      </div>
    </div>
    <FormChangeReasonModal v-model="changeReasonModalVisible" @confirmed="updateEdcEditedForm" :openQueries="openQueries" />
    <!-- Cancel editing form confirmation  Modal Component -->
    <b-modal id="editFormModal" ref="editFormModal" @hidden="closeModal('editFormModal')" hide-footer hide-header :no-close-on-esc="true" :no-close-on-backdrop="true">
      <b-row class="pt-20 width-500 border-style text-center">
        <b-col align-self="center" class="pb-0 pl-0">
          <h2>
            <b>Warning</b>
          </h2>
        </b-col>
      </b-row>
      <b-row class="text-center">
        <b-col align-self="center" class="pb-5 pt-5">Are you sure you want to cancel editing this form?</b-col>
      </b-row>
      <b-row class="pt-20 pb-40">
        <b-col cols="3"></b-col>
        <b-col cols="3">
          <b-btn class="btn-block fs-14" variant="secondary" @click="cancelAndCloseModal('editFormModal')">No</b-btn>
        </b-col>
        <b-col cols="3">
          <b-btn class="btn-block fs-14 error-bg primaryBgColor" variant="primary" @click="cancelEdit(), closeModal('editFormModal')">Yes</b-btn>
        </b-col>
      </b-row>
      <div class="modal-style"></div>
    </b-modal>
  </div>
</template>

<script>
import * as activityService from "@/components/services/activity-service";
import ParticipantInfo from "@/components/participants/participant-info-new";
import EdcFormHistoryList from "@/components/form/edc-form-history-list";
import EdcFormHistoryVersion from "@/components/form/edc-form-history-version";
import EdcFormQueries from "@/components/form/edc-form-queries";
import ButtonCmp from "@/components/common/button";
import FormPreview from "@/components/edc-form/form-preview";
import FormChangeReasonModal from "@/components/modals/form-change-reason-modal";
import SubmittedFormPreview from "@/components/edc-form/submitted-form-preview";
import ActivityFormPreviewColumns from "@/components/activity-form/activity-form-preview-columns";
import EdcFormSourceDocuments from "@/components/form/edc-form-source-documents";
import EdcFormAdditionalScores from "@/components/form/edc-form-additional-scores";
import NavigationBreadcrumb from "@/components/common/navigation-breadcrumb";
import { AclRule } from "vue-acl";
import { mapActions, mapGetters } from "vuex";
import find from "lodash/find";
import difference from "lodash/difference";
import each from "lodash/each";
import findIndex from "lodash/findIndex";
import uniq from "lodash/uniq";
import pull from "lodash/pull";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import _ from "lodash";
import PiiAndNonPiiInfo from "@/components/participants/pii-and-non-pii-info";
import MultilingualService from "@/components/services/multilingual-service";

import { PI, SUB_PI, STUDY_COORDINATOR, HOME_HEALTH, CRA, SITEDATALOCK, STUDY_ADMIN, BSTAT, PHV, INTERVIEWER, CC, CraUB, RATER, SUPPORT, DM, QUALITY } from "@/constants/authRoles";
import { formFieldQueryStatus } from "@/constants/form";
export default {
  name: "study-participant-form",
  components: {
    ParticipantInfo,
    EdcFormHistoryList,
    EdcFormHistoryVersion,
    EdcFormQueries,
    FormPreview,
    ButtonCmp,
    SubmittedFormPreview,
    ActivityFormPreviewColumns,
    FormChangeReasonModal,
    EdcFormSourceDocuments,
    EdcFormAdditionalScores,
    NavigationBreadcrumb,
    PiiAndNonPiiInfo
  },
  props: {
    studyId: {
      type: String
    },
    studyData: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      getOffsetValue: null,
      areActionsAllowed: {
        showDocument: false,
        telehealthCall: true,
        edcforms: false,
        canDisqualifyParticipant: true
      },
      formSubmitted: 1,
      tabIndex: 0,
      participantInfo: null,
      formHistory: [],
      isLoading: true,
      newQuery: null,
      formHistoryVersion: null,
      edit: false,
      filterFieldId: null,
      generalInfo: null,
      participant: null,
      sections: [],
      queries: [],
      comments: [],
      formId: this.$route.params.formId,
      formStudyId: this.$route.params.studyId,
      milestone: this.$route.params.milestone,
      participantId: this.$route.params.participantId,
      responseId: this.$route.params.responseId,
      responseVersionId: "",
      formData: [],
      versionFormData: [],
      formResponses: {},
      answersObj: [],
      response: [],
      responseScores: [],
      editedFields: [],
      diffResp: [],
      edcFormHistoryVersion: null,
      queryIds: [],
      changeReasonModalVisible: false,
      siteTeam: {},
      clickedFieldId: null,
      sourceDocumentsOpen: false,
      documentsList: [],
      siteTeamDetails: [],
      getDocuments: false,
      latestVersionData: {},
      navigationOptions: [
        {
          name: "Profile",
          isLink: true,
          route: "participant-profile",
          params: { studyId: this.$route.params.studyId },
          displayOrder: 2
        },
        {
          name: "Participants",
          isLink: true,
          route: "study-participants-list",
          params: { studyId: this.$route.params.studyId },
          displayOrder: 1
        },
        {
          name: "Form Packet Details",
          isLink: false,
          route: null,
          params: null,
          displayOrder: 3
        }
      ],
      navigationWidth: "55%",
      telehealthCallStatus: false,
      totalScore: 0,
      createdById: "",
      disabledFieldsByBranchingLogic: {},
      triggerValidation: false,
      unsupportedQuestionType: ["ImageCapture", "MedicationSelector", "AudioRecording", "SurveyCompletion", "SurveyInstruction", "VideoRecording", "GPSkin", "Section", "WongBaker"],
      openQueries: [],
      selectedLanguageCulture: "EN",
      selectedLanguageId: "ca0af64b-9a40-11e9-8a9c-12046093208c",
      selectedLanguageName: "English (US)",
      mlService: new MultilingualService(this.$route),
      taskInstanceId: "",
      validationCount: 0,
      isSaveButtonDisabled: false,
      displayAdditionalScoresAtLast: false,
      variablesList: [],
      activityId: "",
      activityRevisionId: ""
    };
  },
  computed: {
    ...mapGetters(["user", "getVideoCallStatus"]),
    fId() {
      return this.$route.params.formId;
    },
    fieldsWithQueries() {
      if (this.queries) {
        return this.queries.filter(q => q.status === formFieldQueryStatus.OPEN).map(q => q.fieldId);
      }
      return [];
    },
    userAccess() {
      return new AclRule(this.user.userRole).generate();
    },
    canStudyAdminAccess() {
      this.$acl.change(`${STUDY_ADMIN}`);
      return this.$acl.check("userAccess");
    },
    canPiAccess() {
      this.$acl.change(`${PI}`);
      return this.$acl.check("userAccess");
    },
    canSubPiAccess() {
      this.$acl.change(`${SUB_PI}`);
      return this.$acl.check("userAccess");
    },
    canRaterAccess() {
      this.$acl.change(`${RATER}`);
      return this.$acl.check("userAccess");
    },
    canScAccess() {
      this.$acl.change(`${STUDY_COORDINATOR}`);
      return this.$acl.check("userAccess");
    },
    canHomeHealthAccess() {
      this.$acl.change(`${HOME_HEALTH}`);
      return this.$acl.check("userAccess");
    },
    canCraAccess() {
      this.$acl.change(`${CRA}`);
      return this.$acl.check("userAccess");
    },
    canCraUBAccess() {
      this.$acl.change(`${CraUB}`);
      return this.$acl.check("userAccess");
    },
    canInterviewerAccess() {
      this.$acl.change(`${INTERVIEWER}`);
      return this.$acl.check("userAccess");
    },
    canBioStatisticsAccess() {
      this.$acl.change(`${BSTAT}`);
      return this.$acl.check("userAccess");
    },
    canPharmacovigilanceAccess() {
      this.$acl.change(`${PHV}`);
      return this.$acl.check("userAccess");
    },
    canSiteDataLockAccess() {
      this.$acl.change(`${SITEDATALOCK}`);
      return this.$acl.check("userAccess");
    },
    canCallCenterAccess() {
      this.$acl.change(`${CC}`);
      return this.$acl.check("userAccess");
    },
    canSupportAccess() {
      this.$acl.change(`${SUPPORT}`);
      return this.$acl.check("userAccess");
    },
    canDmAccess() {
      this.$acl.change(`${DM}`);
      return this.$acl.check("userAccess");
    },
    canQualityAccess() {
      this.$acl.change(`${QUALITY}`);
      return this.$acl.check("userAccess");
    },
    languages() {
      if (this.studyData && this.studyData.language) {
        return this.studyData.language;
      }
      return [];
    },
    languageCulture() {
      return this.selectedLanguageCulture.includes("-") ? this.selectedLanguageCulture : this.selectedLanguageCulture.toLowerCase();
    },
    showAdditionalScores() {
      if (this.variablesList && this.variablesList.length > 0 && this.displayAdditionalScoresAtLast) {
        return true;
      }
      return false;
    }
  },
  watch: {
    getVideoCallStatus(isCallInProgress) {
      this.telehealthCallStatus = isCallInProgress;
    },
    "formData.activityId": {
      deep: true,
      handler(value) {
        this.activityId = value;
        if (this.activityId) {
          this.getActivityVariablesList();
        }
      }
    }
  },
  methods: {
    ...mapActions(["clearEDCFormErrors"]),
    disabledFields(fields, clear = false) {
      this.disabledFieldsByBranchingLogic = fields;
      if (clear) {
        this.validationCount = this.validationCount + 1;
      }
    },
    letMakeOptionalToDisableFields(requiredFieldIds) {
      let fieldsDisabled = [];
      for (const [o, i] of Object.entries(this.disabledFieldsByBranchingLogic)) {
        if (i == true) {
          fieldsDisabled.push(o);
        }
      }

      return difference(requiredFieldIds, fieldsDisabled);
    },
    checkIfEditCheckAllowed(formData) {
      if (formData && formData.type != "EDC" && formData.activity) {
        if (formData.activity.allowDataEditChecks) {
          return true;
        }
        return false;
      }
      return true;
    },
    closeModal(ref) {
      this.$refs[ref].hide();
    },

    cancelAndCloseModal(ref) {
      this.tabIndex = 0;
      this.$refs[ref].hide();
    },

    showModal(ref) {
      this.$refs[ref].show();
    },

    checkEditMode() {
      this.openTab(2);
      if (this.edit) {
        this.showModal("editFormModal");
      }
    },
    piInfoExpanded(isExpanded) {
      let c = document.getElementById("edcFormDetail");
      if (isExpanded) {
        setTimeout(() => {
          window.$("#edcFormDetail").animate({});
        }, 400);
      } else {
        c.style.paddingTop = "0px";
      }
    },
    openChangeReasonModal() {
      this.validationCount = this.validationCount + 1;
      this.changeReasonModalVisible = true;
    },
    // API integration to send Verify / Unverify form
    verificationConfirmed(status) {
      if (this.generalInfo.verificationStatus !== status) {
        const updateData = {
          action: status,
          verifyAction: true
        };
        this.$api
          .updateEdcFormResponseVersionsResource(this.formResponses.responseId, this.latestVersionData.responseVersionId, this.studyId, updateData)
          .then(() => {
            /* this.generalInfo.verificationStatus = status;
            this.getListOfQueries();
            // get the form history versions
            this.getEDCFormHistoryList(); */
            this.getEDCFormDetails(true);
            this.$toastr.success(`${status === "NOT_VERIFIED" ? "Unverification" : "Verification"} confirmed.`);
          })
          .catch(error => {
            const { message = "Error while saving verification status" } = (error && error.body) || {};
            this.$toastr.error(message);
          });
      }
    },

    // API integration to send approve / unapprove
    approvalConfirmed(status) {
      if (this.generalInfo.approvalStatus !== status) {
        const updateData = {
          action: status,
          approveAction: true
        };
        this.$api
          .updateEdcFormResponseVersionsResource(this.formResponses.responseId, this.latestVersionData.responseVersionId, this.studyId, updateData)
          .then(() => {
            /* this.generalInfo.approvalStatus = status;
            this.getListOfQueries();
            // get the form history versions
            this.getEDCFormHistoryList(); */
            this.getEDCFormDetails(true);
            this.$toastr.success(`${status === "APPROVED" ? "Approval confirmed." : "Unapproval confirmed."}`);
          })
          .catch(error => {
            const { message = "Error while saving approval status" } = (error && error.body) || {};
            this.$toastr.error(message);
          });
      }
    },
    reviewConfirmed(status) {
      if (this.generalInfo.reviewStatus !== status) {
        const updateData = {
          action: status,
          reviewAction: true
        };
        this.$api
          .updateEdcFormResponseVersionsResource(this.formResponses.responseId, this.latestVersionData.responseVersionId, this.studyId, updateData)
          .then(() => {
            this.getEDCFormDetails(true);
            this.$toastr.success(`${status === "UNREVIEWED" ? "Unreviewed" : "Reviewed"} confirmed.`);
          })
          .catch(error => {
            const { message = "Error while saving review status" } = (error && error.body) || {};
            this.$toastr.error(message);
          });
      }
    },
    lockConfirmed(status) {
      if (this.generalInfo.lockStatus !== status) {
        const updateData = {
          action: status,
          lockAction: true
        };
        this.$api
          .updateEdcFormResponseVersionsResource(this.formResponses.responseId, this.latestVersionData.responseVersionId, this.studyId, updateData)
          .then(() => {
            this.getEDCFormDetails(true);
            this.$toastr.success(`${status === "UNLOCKED" ? "Unlocked" : "Locked"} confirmed.`);
          })
          .catch(error => {
            const { message = "Error while saving lock status" } = (error && error.body) || {};
            this.$toastr.error(message);
          });
      }
    },
    signoffConfirmed(status) {
      if (this.generalInfo.signoffStatus !== status) {
        let data = [];
        data.push({
          action: status,
          responseId: this.responseId,
          type: this.$route.query.type == "EDC_SCHEDULED" ? "SCHEDULED" : "UNSCHEDULED"
        });

        let requestParams = {
          studyId: this.studyId,
          taskType: "Form"
        };

        this.$api
          .updateSignoff(requestParams, data)
          .then(() => {
            this.getEDCFormDetails();
            this.$toastr.success(`${status === "PENDING" ? "Unsigned off" : "Signed Off"} confirmed.`);
          })
          .catch(error => {
            const { message = "Error while saving sign off status" } = (error && error.body) || {};
            this.$toastr.error(message);
          });
      }
    },
    cancelEdit() {
      this.edit = false;
      this.getEDCFormDetails();
    },
    openQuery(fieldId, offsetValue) {
      this.newQuery = fieldId;
      this.clickedFieldId = fieldId;
      this.getOffsetValue = offsetValue;
    },
    createQuery(query) {
      const reqObj = {
        responseId: this.formResponses.responseId,
        fieldId: query.fieldId,
        responseType: "SCHEDULED",
        comment: {
          cText: query.comment
        },
        assigneeId: query.assigneeId
      };
      this.$api
        .openNewQuery(reqObj, this.formStudyId)
        .then(() => {
          this.$toastr.success("Query created successfully.");
          this.getListOfQueries();
        })
        .catch(error => {
          const { message = "Error while creating field query" } = (error && error.body) || {};
          this.$toastr.error(message);
        });
    },
    createComment(commentData) {
      const obj = {
        cText: commentData.comment
      };
      this.$api
        .addNewComment(commentData.queryId, this.formStudyId, obj)
        .then(() => {
          this.$api.getAllQueryComments(commentData.queryId, this.formStudyId).then(response => {
            this.comments = response.body.comments;
          });
        })
        .catch(error => {
          const { message = "Error while creating comment" } = (error && error.body) || {};
          this.$toastr.error(message);
        });
    },
    updateComment(queryId, fieldId) {
      this.clickedFieldId = fieldId;
      this.$api.getAllQueryComments(queryId, this.formStudyId).then(response => {
        this.comments = response.body.comments;
      });
    },

    // API to fetch the saved form in history
    viewHistoryForm(versionForm) {
      //Prepare a reason for change string to display on the view history form.
      let createdDate = this.$options.filters.formatGMTDate(new Date(versionForm.createdAt));
      let reasonForChange = versionForm.primaryReason ? versionForm.primaryReason : "N/A";
      reasonForChange += " " + createdDate + " by " + versionForm.createdBy;
      this.$api
        .getedcFormResponseVersionsResourceV3(versionForm.responseId, versionForm.responseVerionId, this.formStudyId)
        .then(result => {
          this.formResponses = result.body.response;
          this.versionFormData = result.body.form;
          this.editedFields = result.body.editedFields;
          let formQuestions = null;
          const answersList = this.formResponses.answers;
          // questions and answers object for display
          const answersObj = [];
          if (this.versionFormData.type == "EDC") {
            formQuestions = this.versionFormData.fields;
            each(formQuestions, question => {
              const newObj = {};
              newObj.question = question.body;
              newObj.name = question.name;
              newObj.id = question.id;
              if (question.name !== "formHeader" && question.name !== "formParagraph") {
                newObj.sequence = question.sequence;
              }
              const resObj = find(answersList, answer => answer.questionId === question.id);
              if (resObj) {
                if (resObj.questionName === "formCheckbox" || resObj.questionName === "formTable") {
                  newObj.answerText = resObj.multiAnswer;
                  if (resObj.comment && resObj.comment !== "this is not valid") {
                    newObj.answerText = `${resObj.answerText} - ${resObj.comment}`;
                  }
                  //Implemented this code for Previous values
                  newObj.previousAnswerText = resObj.previousMultiAnswer;
                  newObj.previousScore = resObj.previousScore;

                  if (resObj.previousComment && resObj.previousComment !== "this is not valid") {
                    newObj.previousAnswerText = `${resObj.previousMultiAnswer} - ${resObj.previousComment}`;
                  }
                  //New Code End
                } else if (resObj.questionName === "formDateTime") {
                  newObj.answerText = `${resObj.date || ""} ${resObj.time || ""} ${resObj.timezone || ""}`;
                  newObj.previousAnswerText = `${resObj.previousDate || ""} ${resObj.previousTime || ""} ${resObj.previousTimezone || ""}`;
                  newObj.previousScore = resObj.previousScore;
                } else {
                  newObj.answerText = resObj.answerText;
                  newObj.previousAnswerText = resObj.previousAnswerText;
                  newObj.previousScore = resObj.previousScore;
                  if (resObj.previousComment && resObj.previousComment !== "this is not valid") {
                    newObj.previousAnswerText = `${resObj.previousAnswerText} - ${resObj.previousComment}`;
                  }
                  if (resObj.comment && resObj.comment !== "this is not valid") {
                    newObj.answerText = `${resObj.answerText} - ${resObj.comment}`;
                  }
                }
              } else {
                newObj.answerText = "";
                newObj.computedScore = 0;
                newObj.actualScore = 0;
              }
              newObj.isEditedVersion = this.editedFields && this.editedFields.includes(question.id);
              answersObj.push(newObj);
              this.edcFormHistoryVersion = {
                fields: answersObj,
                primaryReason: reasonForChange,
                type: this.versionFormData.type,
                name: this.versionFormData.name,
                isUnSchedule: false
              };
            });
          } else {
            //CLINRO
            formQuestions = this.versionFormData.activity.questions;
            each(formQuestions, question => {
              const resObj = find(answersList, answer => answer.questionId === question.id);

              if (resObj) {
                if (resObj.questionName === "MultiChoice" || resObj.questionName === "Image") {
                  question.answer = resObj.answerText;
                  question.previousAnswer = resObj.previousAnswerText;
                  let optionsIds = _.cloneDeep(resObj.optionsIds);
                  question.optionsIds = resObj.optionsIds ? optionsIds : [];

                  question.subType = resObj.subType;
                  question.previousSubType = resObj.previousSubType;
                  question.previousOptionsIds = resObj.previousOptionsIds;
                } else if (resObj.questionName === "DateTime") {
                  question.answer = resObj.answerText;
                  question.previousAnswer = resObj.previousAnswerText;
                  question.date = resObj.date;
                  question.time = resObj.time;
                  question.dateFormat = resObj.dateFormat;
                  question.timeFormat = resObj.timeFormat;
                  question.previousDate = resObj.previousDate;
                  question.previousTime = resObj.previousTime;
                  question.previousDateFormat = resObj.previousDateFormat;
                  question.previousTimeFormat = resObj.previousTimeFormat;
                } else if (resObj.questionName == "Boolean") {
                  question.answer = resObj.answerText;
                  question.answerId = resObj.answerId;
                  question.previousAnswer = resObj.previousAnswerText;
                  question.previousAnswerId = resObj.previousAnswerId;
                } else if (resObj.questionName == "Log") {
                  question.multiAnswer = _.cloneDeep(resObj.multiAnswer);
                  question.answerId = resObj.answerId;
                  question.previousMultiAnswer = resObj.previousMultiAnswer;
                  question.previousAnswerId = resObj.previousAnswerId;
                } else if (resObj.questionName == "DropDown") {
                  question.answer = resObj.answerText;
                  question.optionId = resObj.optionId;
                  question.index = resObj.index;
                  question.answerId = resObj.answerId;
                  question.previousAnswer = resObj.previousAnswerText;
                  question.previousAnswerId = resObj.previousAnswerId;
                } else {
                  question.answer = resObj.answerText;
                  question.previousAnswer = resObj.previousAnswerText;
                }
                question.computedScore = resObj.score || 0;
                question.previousScore = resObj.previousScore || 0;
                question.actualScore = resObj.actualScore || 0;
                question.previousActualScore = resObj.previousActualScore || 0;
              } else {
                question.answer = "";
                if (question.type != "Section") {
                  question.computedScore = 0;
                  question.actualScore = 0;
                }
              }
              question.isEditedVersion = this.editedFields && this.editedFields.includes(question.id);
            });
            this.edcFormHistoryVersion = {
              fields: this.versionFormData,
              primaryReason: reasonForChange,
              type: this.versionFormData.type,
              name: this.versionFormData.name,
              isUnSchedule: false
            };
          }
        })
        .catch(error => {
          const { message = "Error while getting form version" } = (error && error.body) || {};
          this.$toastr.error(message);
        });
    },
    historyBack() {
      this.edcFormHistoryVersion = null;
    },
    filterByField(id) {
      this.clickedFieldId = id;
      this.newQuery = null;
    },
    resetFilter() {
      this.newQuery = null;
      this.clickedFieldId = null;
    },
    onQueryClosed(query) {
      this.$api
        .updateQueryStatus(query.queryId, this.formStudyId, "CLOSE")
        .then(() => {
          this.$toastr.success("Query closed");
          this.getListOfQueries();
          this.clickedFieldId = null;
        })
        .catch(error => {
          const { message = "Error while closing query" } = (error && error.body) || {};
          this.$toastr.error(message);
        });
    },
    onQueryReopened(query) {
      this.$api
        .updateQueryStatus(query.queryId, this.formStudyId, "REOPENED")
        .then(() => {
          this.$toastr.success("Query reopened successfully.");
          this.getListOfQueries();
          this.clickedFieldId = null;
        })
        .catch(error => {
          const { message = "Error while reopening query" } = (error && error.body) || {};
          this.$toastr.error(message);
        });
    },

    // get list of all the queries asociated to the form
    getListOfQueries() {
      this.$api.getAllListOfQueries(this.formResponses.responseId, this.formStudyId).then(response => {
        this.queryIds = map(
          response.body.filter(resp => resp.status === "OPEN"),
          "fieldId"
        );
        this.queries = response.body;
        this.queries.forEach((query, queryIndex) => {
          query.comments.forEach((comment, commentIndex) => {
            const tempComment = comment;
            tempComment.createdBy = this.siteTeam[comment.createdBy];
            this.queries[queryIndex].comments[commentIndex] = tempComment;
          });
        });
      });
    },

    getListOfOpenQueries() {
      this.$api.getAllListOfOpenQueries(this.formResponses.responseId, this.formStudyId).then(response => {
        this.openQueries = response.body;
      });
    },

    // get site team members
    async getSiteTeam() {
      try {
        const studyTeamData = await this.$api.getStudyTeamFact(this.$route.params.studyId);
        const siteTeamData = await this.$api.getUserNames(this.$route.params.studyId);

        if (typeof studyTeamData !== "undefined") {
          studyTeamData.body.forEach(team => {
            this.siteTeam[team.id] = `${team.firstName} ${team.lastName}`;
            const siteTeamObj = {};
            siteTeamObj["id"] = team.id;
            siteTeamObj["name"] = `${team.firstName} ${team.lastName}`;
            siteTeamObj["userRole"] = team.userRole;
            this.siteTeamDetails.push(siteTeamObj);
          });
        }

        if (typeof siteTeamData !== "undefined") {
          siteTeamData.body.forEach(team => {
            this.siteTeam[team.id] = `${team.firstName} ${team.lastName}`;
            const siteTeamObj = {};
            siteTeamObj["id"] = team.id;
            siteTeamObj["name"] = `${team.firstName} ${team.lastName}`;
            siteTeamObj["userRole"] = team.userRole;
            this.siteTeamDetails.push(siteTeamObj);
          });
        }
        await this.getParticipantDetails();
        await this.getEDCFormDetails();
      } catch (err) {
        // catches errors both in fetch and response.json
        this.$toastr.error(err.bodyText);
      }
    },

    // get the participant details
    async getParticipantDetails() {
      await this.$api.getParticipantDataFactV2(this.formStudyId, this.participantId).then(
        response => {
          this.participantInfo = response.body;
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting participant.");
        }
      );
    },

    // get edc form history list
    getEDCFormHistoryList() {
      this.$api
        .getEdcQueryFormVersionsHistoryStatus(this.formId, this.formResponses.responseId, this.studyId, "SCHEDULED")
        .then(result => {
          this.formHistory = orderBy(result.body, ["createdAt"], ["desc"]);
          this.formHistory.forEach((form, formIndex) => {
            this.formHistory[formIndex].createdBy = this.siteTeam[form.createdBy];
          });
          this.getDocuments = true;
          // displayAdditionalScoresAtLast made this true so that it can be seen in the last tab
          this.displayAdditionalScoresAtLast = true;
        })
        .catch(error => {
          this.displayAdditionalScoresAtLast = true;
          this.formHistory = [];
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Unable to fetch the form versions history.");
        });
    },
    // get the edc form data
    async getEDCFormDetails(updateEDCFormDetails) {
      await this.$api.getEDCFormResponseByResponseIdFactV3(this.formId, this.formStudyId, this.participantId, this.responseId, this.milestone).then(
        result => {
          this.responseVersionId = result.body.response.responseVersionId;
          let responseId = result.body.response.responseId;
          this.milestone = result.body.milestone;
          this.formResponses = result.body.response;
          this.latestVersionData = result.body.response;
          this.formData = result.body.form;
          this.taskInstanceId = result.body.taskInstanceId;
          if(updateEDCFormDetails)
            this.$root.$emit("edcUpdateScoreValueTab", this.responseVersionId, responseId);

          if (result.body.responseVersionCreatedBy) {
            this.createdById = result.body.responseVersionCreatedBy;
          }
          activityService.setRequiredFieldsOnEachStep(this);
          const answersList = this.formResponses.answers;

          // get the form history versions
          this.getEDCFormHistoryList();
          // get list of queries mapped to this form
          this.getListOfQueries();
          this.getListOfOpenQueries();
          this.getSourceDocuments();
          // questions and answers object for display
          const answersObj = [];
          let formQuestions;
          if (this.formData.type == "EDC") {
            formQuestions = this.formData.fields;
            each(formQuestions, question => {
              const newObj = {};
              newObj.question = question.body;
              newObj.name = question.name;
              newObj.id = question.id;
              if (question.name !== "formHeader" && question.name !== "formParagraph") {
                newObj.sequence = question.sequence;
              }
              const resObj = find(answersList, answer => answer.questionId === question.id);
              if (resObj) {
                if (resObj.questionName === "formCheckbox" || resObj.questionName === "formTable") {
                  if (resObj.questionName === "formCheckbox") {
                    resObj.multiAnswer.forEach(answer => {
                      question.options.forEach(option => {
                        if (answer.optionId === option.id) {
                          if (option.colorCoding) {
                            answer.colorCoding = option.colorCoding;
                          }
                        }
                      });
                    });
                    newObj.noOfColumns = question.noOfColumns;
                  }
                  newObj.answerText = resObj.multiAnswer;
                  if (resObj.comment && resObj.comment !== "this is not valid") {
                    newObj.answerText = `${resObj.answerText} - ${resObj.comment}`;
                  }
                } else if (resObj.questionName === "formDateTime") {
                  newObj.answerText = `${resObj.date || ""} ${resObj.time || ""} ${resObj.timezone || ""}`;
                } else {
                  if (resObj.questionName === "formSinglechoice") {
                    const optionObj = find(question.options, option => resObj.optionId === option.id);
                    if (optionObj.colorCoding) {
                      newObj.colorCoding = optionObj.colorCoding;
                    }
                  }
                  newObj.answerText = resObj.answerText;
                  if (resObj.comment && resObj.comment !== "this is not valid") {
                    newObj.answerText = `${resObj.answerText} - ${resObj.comment}`;
                  }
                }
              } else {
                newObj.answerText = "";
              }
              answersObj.push(newObj);
            });
          } else {
            //CLINRO
            formQuestions = this.formData.activity.questions;
            each(formQuestions, question => {
              const newObj = {};
              newObj.question = question.body;
              newObj.name = question.type;
              newObj.id = question.id;

              if (question.type !== "Section" || question.type !== "SurveyInstruction") {
                newObj.sequence = question.sequence;
              }

              const resObj = find(answersList, answer => answer.questionId === question.id);
              if (resObj) {
                newObj.answerText = resObj.answerText;
                newObj.computedScore = resObj.score;
                newObj.actualScore = resObj.actualScore;
                if (resObj.comment && resObj.comment !== "this is not valid") {
                  newObj.answerText = `${resObj.answerText} - ${resObj.comment}`;
                }
              } else {
                newObj.answerText = "";
                if (question.type != "Section") {
                  newObj.computedScore = 0;
                  newObj.actualScore = 0;
                }
              }
              answersObj.push(newObj);
            });
          }
          this.answersObj = answersObj;
          this.generalInfo = {
            formId: this.formData && this.formData.id ? this.formData.id : result && result.body && result.body.form && result.body.form.id,
            formName: this.formData && this.formData.name ? this.formData.name : result && result.body && result.body.form && result.body.form.name,
            createdTime: result.body && result.body.createdAt ? result.body.createdAt : this.formData.activity.createdTime,
            approvalStatus: result.body.approvalStatus,
            verificationStatus: result.body.verificationStatus,
            lockStatus: result.body.lockStatus,
            signoffStatus: result.body.signoffStatus,
            reviewStatus: result.body.reviewStatus,
            numberOfOpenQueries: null,
            numberOfClosedQueries: null,
            verifiedOn: 0,
            approvedOn: 0,
            submittedOn: null,
            comments: null
          };
          // send the saved answers to the corresponding form components
          if (this.formResponses && this.formResponses.answers && this.formResponses.answers.length) {
            if (this.formData.type == "EDC") {
              // loop through the available answers
              this.formResponses.answers.forEach(answer => {
                // get the field index in the saved formData using answer question ID
                const selectedFieldIndex = findIndex(this.formData.fields, ["id", answer.questionId]);
                // get the option which is saved as answer using option ID
                const selectedOption = find(this.formData.fields[selectedFieldIndex].options, ["id", answer.optionId]);
                // get index of the option in options array which is saved as answer using optionnId
                const selectedOptionIndex = findIndex(this.formData.fields[selectedFieldIndex].options, ["id", answer.optionId]);
                // In formData attach answer as well as the comment if the comment message is saved
                this.formData.fields[selectedFieldIndex].answer = answer.answerText;
                // for singlechoice the answer which is saved need's to be modified as per preview
                if (this.formData.fields[selectedFieldIndex].name === "formSinglechoice" || this.formData.fields[selectedFieldIndex].name === "formDropdown") {
                  this.formData.fields[selectedFieldIndex].answer = selectedOption.id;
                }
                if (this.formData.fields[selectedFieldIndex].name === "formScale") {
                  const selectedScaleOption = find(this.formData.fields[selectedFieldIndex].options, ["id", answer.optionId]);
                  if (selectedScaleOption.type === "NA" || selectedScaleOption.type === "Other") {
                    this.formData.fields[selectedFieldIndex].nonScaleAnswer = answer.answerText;
                    this.formData.fields[selectedFieldIndex].answer = null;
                  }
                }
                if (selectedOption && selectedOption.type === "Other" && selectedOption.commentField) {
                  this.formData.fields[selectedFieldIndex].options[selectedOptionIndex].comment.commentMessage = answer.comment;
                }
                // for checkBox the options array which is saved need's to be modified as per preview
                const tempOptions = [];
                if (this.formData.fields[selectedFieldIndex].name === "formCheckbox") {
                  this.formData.fields[selectedFieldIndex].options.forEach((option, index) => {
                    const tempOption = option;
                    tempOption.answer = { unSelected: `${index + 1}` };
                    tempOption.value = option.label;
                    tempOption.selectVal = `${index + 1}`;
                    tempOptions.push(option);
                  });
                  answer.multiAnswer.forEach(ans => {
                    tempOptions[ans.index].answer = {
                      selected: tempOptions[ans.index].selectVal
                    };
                    if (tempOptions[ans.index].type === "Other" && tempOptions[ans.index].commentField) {
                      tempOptions[ans.index].comment.commentMessage = ans.comment;
                    }
                  });
                  this.formData.fields[selectedFieldIndex].options = tempOptions;
                }
                if (this.formData.fields[selectedFieldIndex].name === "formDateTime") {
                  if (answer.time) {
                    if (this.formData.fields[selectedFieldIndex].timeField.format === "hh:mm a") {
                      const timeVals = answer.time.split(" ");
                      const time = timeVals[0].split(":");
                      this.formData.fields[selectedFieldIndex].time = {
                        hh: time[0],
                        mm: time[1]
                      };
                      this.formData.fields[selectedFieldIndex].meridian = timeVals[1] || null;
                    } else if (this.formData.fields[selectedFieldIndex].timeField.format === "HH:mm") {
                      const time = answer.time.split(":");
                      this.formData.fields[selectedFieldIndex].time = {
                        HH: time[0],
                        mm: time[1]
                      };
                    }
                    this.formData.fields[selectedFieldIndex].timezone = answer.timezone;
                  }
                  if (answer.date) {
                    const dateVals = answer.date.split("/");
                    const dateFormat = pull(uniq(this.formData.fields[selectedFieldIndex].dateField.format), "/");
                    dateFormat.forEach((value, index) => {
                      if (value === "M") {
                        this.formData.fields[selectedFieldIndex].month = dateVals[index];
                      }
                      if (value === "D") {
                        this.formData.fields[selectedFieldIndex].date = dateVals[index];
                      }
                      if (value === "Y") {
                        this.formData.fields[selectedFieldIndex].year = dateVals[index];
                      }
                    });
                  }
                }
                // for table the fields array which is saved need's to be modified as per preview
                const tempTableOptions = [];
                if (this.formData.fields[selectedFieldIndex].name === "formTable") {
                  // for the available fields add the answer which are configured at form creation
                  if (this.formData.fields[selectedFieldIndex].noOfFields) {
                    for (let ind = 0; ind < this.formData.fields[selectedFieldIndex].noOfFields; ind++) {
                      tempTableOptions.push({
                        index: ind,
                        type: "OPTION",
                        label: ""
                      });
                    }
                  }
                  // Add the answer to fields which are not configured
                  // while form creation but added by user
                  const tempTableUserOptions = [];
                  if (answer.multiAnswer.length > this.formData.fields[selectedFieldIndex].noOfFields) {
                    for (let ind = this.formData.fields[selectedFieldIndex].noOfFields; ind < answer.multiAnswer.length; ind++) {
                      tempTableUserOptions.push({
                        index: ind,
                        type: "OPTION",
                        label: ""
                      });
                    }
                  }

                  // attach answers to the generated fields and user added fields
                  answer.multiAnswer.forEach((ans, index) => {
                    if (index < this.formData.fields[selectedFieldIndex].noOfFields) {
                      tempTableOptions[index].label = ans.answerText;
                    }
                    if (index >= this.formData.fields[selectedFieldIndex].noOfFields) {
                      tempTableUserOptions[index - this.formData.fields[selectedFieldIndex].noOfFields].label = ans.answerText;
                    }
                  });
                  this.formData.fields[selectedFieldIndex].fields = tempTableOptions;
                  this.formData.fields[selectedFieldIndex].addFields = tempTableUserOptions;
                }
              });

              // add the mode for each form component to display as preview
              const formFields = [];
              this.formData.fields.forEach(field => {
                const formField = field;
                formField.mode = "saved";
                formFields.push(formField);
              });
              this.formData.fields = formFields;
            } else {
              this.formResponses.answers.forEach(resObj => {
                // const resObj = find(
                //   unscheduleform.response.answers,
                //   (answer) => answer.questionId === question.id
                // );
                let question = this.formData.activity.questions.find(question => question.id == resObj.questionId);
                if (resObj) {
                  if (resObj.questionName === "MultiChoice" || resObj.questionName === "Image") {
                    question.answer = resObj.answerText;
                    let optionsIds = _.cloneDeep(resObj.optionsIds);
                    question.optionsIds = resObj.optionsIds ? optionsIds : [];

                    question.subType = resObj.subType;
                  } else if (resObj.questionName === "DateTime") {
                    question.answer = resObj.answerText;
                    question.date = resObj.date;
                    question.time = resObj.time;
                    question.dateFormat = resObj.dateFormat;
                    question.timeFormat = resObj.timeFormat;
                  } else if (resObj.questionName == "Boolean") {
                    question.answer = resObj.answerText;
                    question.answerId = resObj.answerId;
                  } else if (resObj.questionName == "Log") {
                    question.multiAnswer = _.cloneDeep(resObj.multiAnswer);
                    question.answerId = resObj.answerId;
                  } else if (resObj.questionName == "DropDown") {
                    question.answer = resObj.answerText;
                    question.optionId = resObj.optionId;
                    question.index = resObj.index;
                    question.answerId = resObj.answerId;
                  } else {
                    question.answer = resObj.answerText;
                  }
                  question.computedScore = resObj.score || 0;
                  question.actualScore = resObj.actualScore || 0;
                } else {
                  question.answer = "";
                  if (question.type != "Section") {
                    question.computedScore = 0;
                    question.actualScore = 0;
                  }
                }
              });
            }
          }
          this.isLoading = false;
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting details.");
        }
      );
    },

    // save the edc form edited data
    async updateEdcEditedForm(changeReason, queryId) {
      const currentEdcFormErrors = JSON.parse(window.localStorage.getItem("thread-edcFormErrors")) || [];
      if (find(currentEdcFormErrors, ["comment", true]) || find(currentEdcFormErrors, ["time", true]) || find(currentEdcFormErrors, ["date", true])) {
        this.formSubmitted = this.formSubmitted + 1;
        this.$toastr.error("Please populate required fields.");
      } else {
        this.response = [];
        let answersPreviouslySaved = [];
        if (this.formResponses) {
          answersPreviouslySaved = this.formResponses.answers || [];
        }
        let requiredFieldIds = [];
        let answersIds = [];
        let activityFormErrors = [];
        if (this.formData.type == "EDC") {
          this.formData.fields.forEach(field => {
            this.generateAnswerFormat(field.name, field, answersPreviouslySaved);
          });
          requiredFieldIds = this.formData.fields.filter(field => field.required === true).map(field => field.id);
          // get all the answers filled for each form

          answersIds = this.response.map(answer => answer.questionId);
        } else {
          this.unansweredQuestions = [];
          this.formData.activity.questions.forEach(question => {
            activityService.generateActivityAnswerFormat(question, answersPreviouslySaved, this.response, this.unansweredQuestions);
          });
          requiredFieldIds = this.formData.activity.questions.filter(question => !this.unsupportedQuestionType.includes(question.type) && question.isSkippable == false).map(question => question.id);

          answersIds = this.response.map(answer => answer.questionId);
        }
        requiredFieldIds = this.letMakeOptionalToDisableFields(requiredFieldIds);
        // Toggle the form validation for child components.
        this.triggerValidation = !this.triggerValidation;
        if (difference(requiredFieldIds, answersIds).length) {
          this.formSubmitted = this.formSubmitted + 1;
          this.$toastr.error("Please populate required fields.");
        } else {
          this.diffResp = [];

          if (this.formData.type == "EDC") {
            this.formData.fields.forEach(field => {
              // get the old and new answers given by user
              const oldAnsField = find(this.formResponses.answers, ["questionId", field.id]);
              const newAnsField = find(this.response, ["questionId", field.id]);
              // Update the diffResp array if any of the answer ( old or new ) is not present
              if (oldAnsField && !newAnsField) {
                this.diffResp.push(field.id);
              } else if (!oldAnsField && newAnsField) {
                this.diffResp.push(field.id);
              } else if (oldAnsField && newAnsField) {
                // Check optionId's of old new new answers
                if (oldAnsField.optionId !== newAnsField.optionId) {
                  this.diffResp.push(field.id);
                } else if (field.name === "formDateTime") {
                  // Check answer date and time and update the flag if changed
                  if (oldAnsField.date !== newAnsField.date || oldAnsField.time !== newAnsField.time || oldAnsField.timezone !== newAnsField.timezone) {
                    this.diffResp.push(field.id);
                  }
                } else if (field.name === "formCheckbox") {
                  // Check for answers length update the flag if lengths are different
                  if (oldAnsField.multiAnswer.length !== newAnsField.multiAnswer.length || (!newAnsField.multiAnswer.length && oldAnsField.multiAnswer.length > 0) || (newAnsField.multiAnswer.length && !oldAnsField.multiAnswer.length)) {
                    this.diffResp.push(field.id);
                  } else if (oldAnsField.multiAnswer.length === newAnsField.multiAnswer.length) {
                    // If answers length are equal check for the option Id's
                    // if changed and update the status
                    const existingAns = oldAnsField.multiAnswer.map(multiAns => multiAns.optionId);
                    const newAns = newAnsField.multiAnswer.map(multiAns => multiAns.optionId);
                    if (difference(newAns, existingAns).length > 0 || difference(existingAns, newAns).length > 0) {
                      this.diffResp.push(field.id);
                    }
                    // If answers length and options ID's are equal check for the comments
                    // if changed and update the status
                    const existingCommentsAns = oldAnsField.multiAnswer.map(multiAns => multiAns.comment);
                    const newCommentsAns = newAnsField.multiAnswer.map(multiAns => multiAns.comment);
                    if (difference(newCommentsAns, existingCommentsAns).length > 0 || difference(existingCommentsAns, newCommentsAns).length > 0) {
                      this.diffResp.push(field.id);
                    }
                  }
                } else if (field.name === "formTable") {
                  // Check for answers length update the flag if lengths are different
                  if (oldAnsField.multiAnswer.length !== newAnsField.multiAnswer.length || (!newAnsField.multiAnswer.length && oldAnsField.multiAnswer.length > 0) || (newAnsField.multiAnswer.length && !oldAnsField.multiAnswer.length)) {
                    this.diffResp.push(field.id);
                  } else if (oldAnsField.multiAnswer.length === newAnsField.multiAnswer.length) {
                    // If answers length are equal check for the answerTexts
                    // if changed and update the status
                    const existingAns = oldAnsField.multiAnswer.map(multiAns => multiAns.answerText);
                    const newAns = newAnsField.multiAnswer.map(multiAns => multiAns.answerText);
                    if (difference(newAns, existingAns).length > 0 || difference(existingAns, newAns).length > 0) {
                      this.diffResp.push(field.id);
                    }
                  }
                } else if (field.name === "formTextArea" || field.name === "formScale" || field.name === "formTextNumeric") {
                  // Check answer text of old new new answers
                  if (oldAnsField.answerText !== newAnsField.answerText) {
                    this.diffResp.push(field.id);
                  }
                  // Check answer's comment of old new new answers if present
                  if (oldAnsField.comment && newAnsField.comment) {
                    if (oldAnsField.comment !== newAnsField.comment) {
                      this.diffResp.push(field.id);
                    }
                  } else if ((!newAnsField.comment && oldAnsField.comment) || (newAnsField.comment && !oldAnsField.comment)) {
                    this.diffResp.push(field.id);
                  }
                } else if (field.name === "formDropdown" || field.name === "formSinglechoice") {
                  // Check answer text of old new new answers
                  if (oldAnsField.comment && newAnsField.comment && oldAnsField.comment !== newAnsField.comment) {
                    this.diffResp.push(field.id);
                  }
                }
              }
            });
          } else {
            this.formData.activity.questions.forEach(question => {
              const oldAnswer = find(this.formResponses.answers, ["questionId", question.id]);

              if (question.type == "MultiChoice" || question.type == "Image") {
                if (!oldAnswer) {
                  if (question.optionsIds && question.optionsIds.length) {
                    this.diffResp.push(question.id);
                  }
                } else {
                  if (question.optionsIds instanceof Array) {
                    let currentOptionIds = question.optionsIds;
                    let oldOptionIds = oldAnswer.optionsIds;
                    const currentOtherOption = question.options && question.options.length && question.options.find(({ id }) => id === question.otherOptionId);
                    const currentOtherOptionText = currentOtherOption && currentOtherOption.body;

                    if (currentOptionIds.length != (oldOptionIds && oldOptionIds.length)) {
                      this.diffResp.push(question.id);
                      return;
                    }
                    
                    if (difference(currentOptionIds, oldOptionIds).length) {
                      this.diffResp.push(question.id);
                      return;
                    }
                    
                    // TODO: find a better way to check if Other option comment changed
                    if (oldAnswer.optionsIds &&
                      oldAnswer.optionsIds.length &&
                      oldAnswer.answerText &&
                      oldAnswer.optionsIds.some(id => id === question.otherOptionId) &&
                      !oldAnswer.answerText.endsWith(currentOtherOptionText)) {
                      this.diffResp.push(question.id);
                      return;
                    }
                  }
                }
              } else if (question.type == "Log") {
                if (!oldAnswer) {
                  if (question.multiAnswer && question.multiAnswer.length) {
                    this.diffResp.push(question.id);
                  }
                } else {
                  let isDiff = activityService.getDiffOfArray(oldAnswer.multiAnswer, question.multiAnswer);
                  if (isDiff) {
                    this.diffResp.push(question.id);
                  }
                }
              } else if (question.type == "Boolean") {
                if (!oldAnswer) {
                  if (question.answerId || question.answerId === 0) {
                    this.diffResp.push(question.id);
                  }
                } else {
                  if (question.answerId != oldAnswer.answerId) {
                    this.diffResp.push(question.id);
                  }
                }
              } else if (question.type == "Index") {
                if (!oldAnswer) {
                  if (question.answerId || question.answerId === 0) {
                    this.diffResp.push(question.id);
                  }
                } else {
                  if (question.answerId !== oldAnswer.answerId) {
                    if (oldAnswer && oldAnswer.answerText && question && question.answer) {
                      const answerValue = this.$options.filters.stripTags(question.answer.value);
                      const oldAnswerValue = this.$options.filters.stripTags(oldAnswer.answerText.value);
                      if(oldAnswerValue !== answerValue) {
                        this.diffResp.push(question.id);
                      }
                    }
                  }
                }
              } else if (question.type == "DateTime") {
                if (!oldAnswer) {
                  if (question.date || question.time) {
                    this.diffResp.push(question.id);
                  }
                } else {
                  if (question.date != oldAnswer.date || question.time != oldAnswer.time) {
                    this.diffResp.push(question.id);
                  }
                }
              } else if (question.type == "CompletionQuestion") {
                // Do nothing
              } else {
                if (!oldAnswer) {
                  if (question.answer || question.answer === 0) {
                    this.diffResp.push(question.id);
                  }
                } else {
                  if (question.answer != oldAnswer.answerText) {
                    this.diffResp.push(question.id);
                  }
                }
              }
            });
            this.formData.activity.questions.forEach(question => {
              if (question.hasFieldError) {
                activityFormErrors.push("Please fill valid data");
              }
            });
          }
          const errorText = await this.validateRequiredFields("sleep");
          if (this.diffResp.length === 0) {
            this.$toastr.error("No new changes were made in the form.");
          } else if (errorText === "requiredError") {
            this.$toastr.error("Please fill all required data.");
          } else if (errorText === "invalidData") {
            this.$toastr.error("Please fill Valid Data.");
          } else if (activityFormErrors.length) {
            this.$toastr.error(activityFormErrors[0]);
          } else {
            const updateData = {
              form: this.formData,
              taskInstanceId: this.taskInstanceId,
              milestone: this.milestone,
              participantId: this.participantId,
              response: {
                state: "SUBMITTED",
                answers: this.response,
                languageId: this.selectedLanguageId,
                languageName: this.selectedLanguageName,
                totalScore: 0,
                sectionScores: {}
              },
              editedFields: this.diffResp,
              primaryReason: changeReason,
              queryId: queryId
            };
            if (this.formResponses) {
              updateData.response.responseId = this.formResponses.responseId;
              updateData.response.responseVersion = this.formResponses.responseVersion;
              updateData.response.responseVersionId = this.formResponses.responseVersionId;
            }
            this.isSaveButtonDisabled = true;
            this.$api
              .updateEDCFormResponseFactV3(this.formId, this.studyId, this.participantId, this.formResponses.responseId, updateData)
              .then(response => {
                if (response.body.response.responseId) {
                  // clear all the available errorflags from Vue store
                  this.clearEDCFormErrors();
                  this.edit = false;
                  this.getEDCFormDetails(true);
                  // get the form history versions
                  this.getEDCFormHistoryList();
                }
              })
              .catch(error => {
                const { message = "Error while updating the form" } = (error && error.body) || {};
                this.$toastr.error(message);
              })
              .finally(() => {
                this.isSaveButtonDisabled = false;
              });
          }
        }
      }
    },
    validateDataForScoring() {
      this.response = [];
      let answersPreviouslySaved = [];
      if (this.formResponses) {
        answersPreviouslySaved = this.formResponses.answers || [];
      }
      if (this.formData.type == "EDC") {
        this.formData.fields.forEach(field => {
          this.generateAnswerFormat(field.name, field, answersPreviouslySaved);
        });
      } else {
        if (this.formData) {
          this.unansweredQuestions = [];
          this.formData.activity.questions.forEach(question => {
            activityService.generateActivityAnswerFormat(question, answersPreviouslySaved, this.response, this.unansweredQuestions);
          });

          //this code need to be removed once backend advanced scoring changes are removed
          let responseScore = {};
          responseScore.totalScore = 0;
          responseScore.sectionScores = {};
          this.responseScores[0] = responseScore;
          this.$set(this.responseScores, 0, responseScore);
          //this code need to be removed once backend advanced scoring changes are removed
        }
      }
    },
    // get answer format for the field given
    generateAnswerFormat(fieldName, field, answers) {
      switch (fieldName) {
        case "formDropdown": {
          if (field.answer) {
            const answerSelected = find(field.options, ["id", field.answer]);
            const isAnswerAlreadySaved = find(answers, ["questionId", field.id]);
            const dropDownResponse = {
              questionId: field.id,
              questionName: field.name,
              optionId: answerSelected.id,
              answerText: answerSelected.label,
              index: answerSelected.index
            };
            if (isAnswerAlreadySaved) {
              dropDownResponse.answerId = isAnswerAlreadySaved.answerId;
            }
            if (answerSelected.type === "Other" && answerSelected.commentField) {
              dropDownResponse.comment = answerSelected.comment.commentMessage;
            }
            this.response.push(dropDownResponse);
          }
          break;
        }
        case "formCheckbox": {
          const checkBoxResponse = {
            questionId: field.id,
            questionName: field.name,
            multiAnswer: []
          };
          const areAnswersAlreadySaved = find(answers, ["questionId", field.id]);
          field.options.forEach(option => {
            if (option.answer && option.answer.selected) {
              const tempCheckBoxResponse = {
                optionId: option.id,
                answerText: option.label,
                index: option.index
              };
              if (option.type === "Other" && option.commentField) {
                tempCheckBoxResponse.comment = option.comment.commentMessage;
              }
              checkBoxResponse.multiAnswer.push(tempCheckBoxResponse);
            }
          });
          if (areAnswersAlreadySaved) {
            checkBoxResponse.answerId = areAnswersAlreadySaved.answerId;
          }
          if (checkBoxResponse.multiAnswer.length > 0) {
            this.response.push(checkBoxResponse);
          }
          break;
        }
        case "formTextNumeric": {
          if (field.answer) {
            const isAnswerAlreadySaved = find(answers, ["questionId", field.id]);
            const textNumericResponse = {
              questionId: field.id,
              questionName: field.name,
              answerText: field.answer
            };
            if (isAnswerAlreadySaved) {
              textNumericResponse.answerId = isAnswerAlreadySaved.answerId;
            }
            this.response.push(textNumericResponse);
          }
          break;
        }
        case "formSinglechoice": {
          if (field.answer) {
            const answerSelected = find(field.options, ["id", field.answer]);
            const isAnswerAlreadySaved = find(answers, ["questionId", field.id]);
            const singleChoiceResponse = {
              questionId: field.id,
              questionName: field.name,
              optionId: answerSelected.id,
              answerText: answerSelected.label,
              index: answerSelected.index
            };
            if (isAnswerAlreadySaved) {
              singleChoiceResponse.answerId = isAnswerAlreadySaved.answerId;
            }
            if (answerSelected.type === "Other" && answerSelected.commentField) {
              singleChoiceResponse.comment = answerSelected.comment.commentMessage;
            }
            this.response.push(singleChoiceResponse);
          }
          break;
        }
        case "formScale": {
          if (field.answer && !field.nonScaleAnswer) {
            const answerSelected = find(field.options, ["label", field.answer]);
            const isAnswerAlreadySaved = find(answers, ["questionId", field.id]);
            const scaleResponse = {
              questionId: field.id,
              questionName: field.name,
              optionId: answerSelected.id,
              answerText: answerSelected.label,
              weight: answerSelected.weight,
              index: answerSelected.index
            };
            if (isAnswerAlreadySaved) {
              scaleResponse.answerId = isAnswerAlreadySaved.answerId;
            }
            if (answerSelected.type === "Other" && answerSelected.commentField) {
              scaleResponse.comment = answerSelected.comment.commentMessage;
            }
            this.response.push(scaleResponse);
          }
          if (field.nonScaleAnswer) {
            const answerSelected = find(field.options, ["label", field.nonScaleAnswer]);
            const isAnswerAlreadySaved = find(answers, ["questionId", field.id]);
            const scaleResponse = {
              questionId: field.id,
              questionName: field.name,
              optionId: answerSelected.id,
              answerText: answerSelected.label,
              index: answerSelected.index,
              weight: answerSelected.weight
            };
            if (isAnswerAlreadySaved) {
              scaleResponse.answerId = isAnswerAlreadySaved.answerId;
            }
            if (answerSelected.type === "Other" && answerSelected.commentField) {
              scaleResponse.comment = answerSelected.comment.commentMessage;
            }
            this.response.push(scaleResponse);
          }
          break;
        }
        case "formDateTime": {
          const isAnswerAlreadySaved = find(answers, ["questionId", field.id]);
          const dateTimeResponse = {
            questionId: field.id,
            questionName: field.name,
            timezone: field.timezone
          };
          if (isAnswerAlreadySaved) {
            dateTimeResponse.answerId = isAnswerAlreadySaved.answerId;
          }
          if (field.collectDate && field.dateField.format) {
            const dateFormat = pull(uniq(field.dateField.format), "/").join("");
            dateTimeResponse.date = "";
            dateFormat.split("").forEach(val => {
              if (val === "M" && field.month) {
                dateTimeResponse.date += `${field.month}/`;
              }
              if (val === "Y" && field.year) {
                dateTimeResponse.date += `${field.year}/`;
              }
              if (val === "D" && field.date) {
                dateTimeResponse.date += `${field.date}/`;
              }
            });
            if (dateTimeResponse.date.length > 0) {
              dateTimeResponse.date = dateTimeResponse.date.slice(0, -1);
            }
          }
          if (field.collectTime && field.timeField.format) {
            if (field.timeField.format === "hh:mm a" && field.time.hh && field.time.mm) {
              dateTimeResponse.time = `${field.time.hh}:${field.time.mm} ${field.meridian}`;
            }
            if (field.timeField.format === "HH:mm" && field.time.HH && field.time.mm) {
              dateTimeResponse.time = `${field.time.HH}:${field.time.mm}`;
            }
          }
          if ((dateTimeResponse.date && dateTimeResponse.date.length > 0) || (dateTimeResponse.time && dateTimeResponse.time.length > 0)) {
            this.response.push(dateTimeResponse);
          }
          break;
        }
        case "formTable": {
          const tableResponse = {
            questionId: field.id,
            questionName: field.name,
            multiAnswer: []
          };
          const areAnswersAlreadySaved = find(answers, ["questionId", field.id]);
          field.fields.forEach(option => {
            if (option.label) {
              tableResponse.multiAnswer.push({
                answerText: option.label
              });
            }
          });
          field.addFields.forEach(option => {
            if (option.label) {
              tableResponse.multiAnswer.push({
                answerText: option.label
              });
            }
          });
          if (areAnswersAlreadySaved) {
            tableResponse.answerId = areAnswersAlreadySaved.answerId;
          }
          if (tableResponse.multiAnswer.length > 0) {
            this.response.push(tableResponse);
          }
          break;
        }
        case "formTextArea": {
          if (field.answer) {
            const isAnswerAlreadySaved = find(answers, ["questionId", field.id]);
            const textAreaResponse = {
              questionId: field.id,
              questionName: field.name,
              answerText: field.answer
            };
            if (isAnswerAlreadySaved) {
              textAreaResponse.answerId = isAnswerAlreadySaved.answerId;
            }
            this.response.push(textAreaResponse);
          }
          break;
        }
        default:
          break;
      }
    },

    goToPreviousScreen() {
      const studyID = this.studyId;
      this.$router.push({
        name: "study-participant-details",
        params: { studyID, participantId: this.participantId }
      });
    },

    // update the flag from the form fields after the form submitted
    formSubmittedFlag(value) {
      this.formSubmitted = value;
    },
    openTab(tabNumber) {
      switch (tabNumber) {
        case 1:
          this.sourceDocumentsOpen = false;
          break;
        case 2:
          this.sourceDocumentsOpen = false;
          break;
        case 3:
          this.sourceDocumentsOpen = true;
          break;
        case 4:
          this.sourceDocumentsOpen = false;
          break;
      }
    },
    getSourceDocuments() {
      this.$api.getSourceDocumentsFact(this.studyId, this.participantId, this.formResponses.responseId, this.participantInfo.siteId).then(
        response => {
          this.documentsList = orderBy(response.body, "name");
          this.documentsList.forEach(document => {
            const documentIndex = findIndex(this.siteTeamDetails, ["id", document.createdBy]);
            document.createdByName = this.siteTeamDetails[documentIndex].name;
            let createdByRole = this.siteTeamDetails[documentIndex].userRole;
            if (createdByRole === "Cra") {
              document.createdByUserRole = "CRA(B)";
            } else if (createdByRole === "CraUB") {
              document.createdByUserRole = "CRA(U)";
            } else if (createdByRole === "Pi") {
              document.createdByUserRole = "PI";
            } else if (createdByRole === "SubPi") {
              document.createdByUserRole = "Sub-I";
            } else if (createdByRole === "StudyCoordinator") {
              document.createdByUserRole = "SC";
            } else if (createdByRole === "HomeHealth") {
              document.createdByUserRole = "Home Health";
            } else if (createdByRole === "CallCenter") {
              document.createdByUserRole = "Call Center";
            }
            const fileSize = (document.size / 1024).toFixed(2);
            if (fileSize < 1024) {
              document.fileSize = fileSize + " KB";
            } else {
              document.fileSize = (fileSize / 1024).toFixed(2) + " MB";
            }
          });
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting source documents");
        }
      );
    },
    toggleParticipant(participant) {
      let params = {
        studyId: this.studyId,
        formId: this.formId,
        milestone: this.milestone,
        responseId: this.responseId || "0"
      };
      this.$router.push({ name: "submitted-form-new", params: params });
      this.participantId = participant.id;
      this.getSiteTeam();
    },
    onLanguageChange(lang) {
      this.selectedLanguageCulture = lang;
      for (let index = 0; index < this.languages.length; index++) {
        if (lang == this.languages[index].language_culture) {
          this.selectedLanguageId = this.languages[index].language_id;
          this.selectedLanguageName = this.languages[index].name;
          this.getApplangaData(this.languages[index].language_id);
        }
      }
    },
    getApplangaData(languageId) {
      this.$nextTick(() => {
        this.mlService.getCSSRTranslation(this.$route.params.studyId, languageId);
      });
    },
    async validateRequiredFields(data = null) {
      if (data == "sleep") {
       await activityService.sleep(1000);
      }
      let hasError = "";
      if (this.formData) {
        this.formData.activity.questions.map(question => {
          if (!question.isSkippable && question.type !== "SurveyCompletion" && !this.disabledFieldsByBranchingLogic[question.id]) {
            if (activityService.validateRequiredFields(question)) {
              hasError = "requiredError";
            }
          }
          if (question.isSkippable && question.type == "Log" && activityService.checkLogEntryEmptyAnswers(question) && !this.disabledFieldsByBranchingLogic[question.id]) {
            hasError = "invalidData";
          }
        });
      }
      return hasError;
    },
    getActivityVariablesList() {
      let that = this;
      this.activityRevisionId = this.formData.activity.id;
      activityService.getActivityVariablesList(this.activityRevisionId).then(
        res => {
          that.variablesList = res;
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting variable list.");
        }
      );
    }
  },
  mounted() {
    this.getSiteTeam();
  }
};
</script>
<style scoped lang="scss" src="../../../public/static/fonts.scss"></style>
<style lang="scss">
@import "~@/assets/variables.scss";
.primaryBgColor {
  background-color: #1e8fe1;
  border-color: #1e8fe1;
}
.primaryBgColor {
  background-color: #1e8fe1;
}

.width-500 {
  width: 500px !important;
}
.component {
  font-family: Roboto-Regular;
  background-color: #fff;
  font-size: 14px;
  //min-height: 900px;
  .nav-breadcrumb {
    display: block;
    padding: 10px 4px;
  }
}
.pi-info-bars {
  background: #fff;
}
.clear-float {
  clear: both;
}
.study-particip-form-block {
  .nav-tabs {
    border-bottom-color: #d3dfe4;
    .nav-item {
      margin: 0 17px;
    }

    .nav-link {
      line-height: 1.43;
      color: #2d3752;
      padding: 0.9rem 0 0.45rem !important;
      border: none;
      border-bottom: 5px solid transparent;

      &:focus,
      &:hover {
        border: none;
        border-bottom: 5px solid transparent;
      }

      &:hover {
        border-bottom: 5px solid #4c8ce4;
      }

      &.active {
        font-weight: bold;
        border-bottom: 5px solid #4c8ce4;
      }
    }
  }
  .tabs-queries {
    border-left: 2px solid #ced4da;

    .nav-tabs {
      margin-left: -1px;
      border-left: 1px solid #fff;
      flex-wrap: inherit;
    }
  }
}

@media (max-width: 1400px) {
  .study-particip-form-block {
    .nav-tabs {
      .nav-item {
        margin: 0 10px;
      }
    }
  }
}
.telehealth-call {
  .study-particip-form-block .nav-tabs .nav-link {
    font-size: 12px;
  }
}
.d-inline-block.score_total {
  width: 75%;
  text-align: right;
  line-height: 36px;
}
</style>
